/* Workspace — AI Center (AI 應用工具)
 * 集中放置有趣的業外 AI 工具
 */

function AICenterPage() {
  const toast = useToast();
  const [activeModal, setActiveModal] = React.useState(null); // 'voice' | 'canvas' | 'code' | null

  const tools = [
    {
      // YTKB — 內網 GB10 的 YouTube 音軌與字幕工作台，經 portal 反向代理
      // /api/ytkb/*（見 routes_ytkb.py）直開新分頁使用；上游另有自己的站內登入。
      id: 'ytkb',
      title: 'YTKB 影音知識庫',
      italic: 'YouTube 音軌與字幕工作台',
      desc: '抓取 YouTube 頻道影音的音軌並 AI 轉錄字幕，支援批次佇列、頻道包策展與知識庫發佈的一站式工作台。',
      icon: 'book',
      badge: 'NEW',
      badgeTone: 'accent',
      actionText: '開啟工具',
      onClick: () => {
        openYtkbTab(toast);
      }
    },
    {
      id: 'voice',
      title: '語音摘要助理',
      italic: 'Voice Summarizer',
      desc: '上傳會議或市場語音檔案，由 AI 自動生成結構化摘要與重要決策待辦事項。',
      icon: 'message',
      badge: '實驗室',
      badgeTone: 'warn',
      actionText: '啟動工具',
      onClick: () => {
        setActiveModal('voice');
      }
    },
    {
      id: 'canvas',
      title: 'AI 畫作生成',
      italic: 'AI Canvas',
      desc: '輸入文字描述，自動生成符合品牌或行銷風格的精美視覺插圖與概念設計。',
      icon: 'layout',
      badge: '實驗室',
      badgeTone: 'warn',
      actionText: '啟動工具',
      onClick: () => {
        setActiveModal('canvas');
      }
    },
    {
      id: 'code',
      title: '智慧合規審查',
      italic: 'Code Guard',
      desc: '上傳合規程式碼或交易策略指令，進行自動化風險審查與潛在邏輯漏洞檢查。',
      icon: 'file-text',
      badge: '實驗室',
      badgeTone: 'warn',
      actionText: '啟動工具',
      onClick: () => {
        setActiveModal('code');
      }
    }
  ];

  return (
    <>
      <PageHeader
        eyebrow="工作空間 · AI"
        title="AI 應用工具"
        italic="Genesis AI Hub"
      />

      <div style={uiStyle({
        fontSize: FontSizes.xs,
        color: HR.inkSoft,
        lineHeight: 1.6,
        marginBottom: 24,
        background: HR.surfaceAlt,
        padding: '12px 16px',
        borderRadius: 6,
        borderLeft: `3px solid ${HR.accent}`,
      }, { fontSize: 14, color: GxT.color.onPc, background: GxT.color.pc, padding: '14px 18px', borderRadius: GxT.radius.inner, borderLeft: 'none' })}>
        💡 歡迎使用 Genesis AI 應用工具箱！此處專門用於集中收納與展示我們團隊後續開發的各類有趣的業外實驗性應用，旨在提升您的日常交易與工作效率。
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: 20,
        marginBottom: 40
      }}>
        {tools.map(t => (
          <Card
            key={t.id}
            padding="24px"
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              minHeight: 220,
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
          >
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={uiStyle({
                  width: 38, height: 38, borderRadius: 8,
                  background: HR.accentSoft, color: HR.accent,
                  display: 'grid', placeItems: 'center',
                }, { width: 48, height: 48, borderRadius: GxT.radius.inner, color: GxT.color.onPc })}>
                  <Icon name={t.icon} size={uiPick(20, 22)} />
                </div>
                <Pill tone={t.badgeTone}>{t.badge}</Pill>
              </div>

              <h3 style={uiStyle({
                fontFamily: HR.serif, fontSize: FontSizes.sm, fontWeight: 500,
                color: HR.ink, margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: 6
              }, { fontSize: GxT.size.h3, fontWeight: GxT.weight.title, flexWrap: 'wrap', gap: '2px 8px' })}>
                {t.title}
                <span style={uiStyle({ fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontWeight: 400 },
                  { fontFamily: GxT.font, fontSize: 12, color: GxT.color.onV, fontWeight: 600 })}>
                  ({t.italic})
                </span>
              </h3>

              <p style={uiStyle({
                fontSize: FontSizes.trading_md, color: HR.inkSoft,
                lineHeight: 1.5, margin: '8px 0 16px 0', flex: 1
              }, { fontSize: 14, lineHeight: 1.6 })}>
                {t.desc}
              </p>
            </div>

            <Button
              variant="secondary"
              onClick={t.onClick}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6
              }}
            >
              <span>{t.actionText}</span>
              <Icon name="chevron-right" size={12} />
            </Button>
          </Card>
        ))}
      </div>

      {activeModal && (
        <Modal
          open
          onClose={() => setActiveModal(null)}
          title="功能開發中"
          subtitle="Feature Under Development"
          footer={
            <Button variant="primary" onClick={() => setActiveModal(null)}>確認</Button>
          }
        >
          <div style={{ textAlign: 'center', padding: '24px 12px' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🛠️</div>
            <h4 style={uiStyle({ fontFamily: HR.serif, fontSize: FontSizes.sm, fontWeight: 500, color: HR.ink, marginBottom: 8 },
              { fontSize: GxT.size.h3, fontWeight: GxT.weight.title })}>
              {tools.find(t => t.id === activeModal)?.title}
            </h4>
            <p style={{ fontSize: FontSizes.xs, color: HR.inkSoft, lineHeight: 1.6 }}>
              此項目為有趣的業外 AI 實驗室工具，目前正在積極規劃與對接中，敬請期待！
            </p>
          </div>
        </Modal>
      )}
    </>
  );
}

window.AICenterPage = AICenterPage;
